import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Upload } from "lucide-react";
import { api, ApiError, SCREENSAVER_DOWNLOAD_URL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface AppInfo {
  available: boolean;
  size?: number;
  uploadedAt?: number | null;
}

function formatSize(bytes?: number) {
  return bytes ? `${(bytes / (1024 * 1024)).toFixed(0)} MB` : "";
}

/** Admin card to download + upload the distributable Windows screensaver. */
export function ScreensaverAppCard() {
  const [info, setInfo] = useState<AppInfo | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  async function refresh() {
    try {
      setInfo(await api.appInfo());
    } catch {
      setInfo({ available: false });
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function onFile(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      setInfo(await api.uploadAppBuild(file));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Upload failed");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Windows screensaver app</CardTitle>
        <CardDescription>
          Staff can download and install it from{" "}
          <Link to="/download" className="underline" target="_blank">
            this link
          </Link>
          {info?.available && info.size ? ` · current build ${formatSize(info.size)}` : ""}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-3">
        {info?.available ? (
          <Button asChild variant="outline" size="sm">
            <a href={SCREENSAVER_DOWNLOAD_URL} download>
              <Download className="mr-1 h-4 w-4" /> Download .scr
            </a>
          </Button>
        ) : (
          // `disabled` doesn't apply to an <a>, so render a real disabled
          // button until a build has been uploaded.
          <Button variant="outline" size="sm" disabled>
            <Download className="mr-1 h-4 w-4" /> Download .scr
          </Button>
        )}
        <Button size="sm" disabled={uploading} onClick={() => fileInput.current?.click()}>
          <Upload className="mr-1 h-4 w-4" />
          {uploading ? "Uploading…" : info?.available ? "Replace build" : "Upload build (.scr)"}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".scr,application/octet-stream"
          className="hidden"
          onChange={(e) => void onFile(e.target.files?.[0])}
        />
        {!info?.available && info !== null && (
          <span className="text-xs text-muted-foreground">
            No build uploaded yet — build it (windows-screensaver) and upload the .scr here.
          </span>
        )}
        {error && <span className="text-xs text-destructive">{error}</span>}
      </CardContent>
    </Card>
  );
}
